/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-10-06
 */

import { createTheme, Theme, PaletteOptions } from '@mui/material/styles';
import {
  ThemeTokens,
  GeneratedColorPalettes,
  ColorPalette,
} from '../types/theme';

/**
 * Convert our ColorPalette to MUI palette structure
 */
const convertColorPalette = (palette: ColorPalette, mainColor: string) => ({
  50: palette[50],
  100: palette[100],
  200: palette[200],
  300: palette[300],
  400: palette[400],
  500: palette[500], // This should match mainColor
  600: palette[600],
  700: palette[700],
  800: palette[800],
  900: palette[900],
  main: mainColor,
  light: palette[300],
  dark: palette[700],
  contrastText: '#ffffff', // Will be calculated based on contrast
});

/**
 * Calculate contrast text color for a given background
 */
const getContrastText = (backgroundColor: string): string => {
  // Simple contrast calculation - in production, use more sophisticated method
  const hex = backgroundColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  // Calculate relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  return luminance > 0.5 ? '#000000' : '#ffffff';
};

/**
 * Create MUI theme from our theme tokens and generated palettes
 */
export const createMUIThemeFromTokens = (
  tokens: ThemeTokens,
  palettes?: GeneratedColorPalettes | null
): Theme => {
  // Add null safety check for tokens
  if (!tokens || !tokens.colors) {
    // Return a basic MUI theme as fallback
    return createTheme({
      palette: {
        mode: 'light',
        primary: { main: '#1976d2' },
        secondary: { main: '#dc004e' },
        error: { main: '#dc3545' },
        warning: { main: '#ffc107' },
        success: { main: '#28a745' },
      },
    });
  }

  // Build palette options
  const paletteOptions: PaletteOptions = {
    mode: 'light', // Start with light mode
    primary: palettes
      ? {
          ...convertColorPalette(palettes.primary, tokens.colors.primary),
          contrastText: getContrastText(tokens.colors.primary),
        }
      : {
          main: tokens.colors.primary,
          contrastText: getContrastText(tokens.colors.primary),
        },
    secondary: palettes
      ? {
          ...convertColorPalette(palettes.secondary, tokens.colors.secondary),
          contrastText: getContrastText(tokens.colors.secondary),
        }
      : {
          main: tokens.colors.secondary,
          contrastText: getContrastText(tokens.colors.secondary),
        },
    error: palettes
      ? {
          ...convertColorPalette(palettes.error, tokens.colors.error),
          contrastText: getContrastText(tokens.colors.error),
        }
      : {
          main: tokens.colors.error,
          contrastText: getContrastText(tokens.colors.error),
        },
    warning: palettes
      ? {
          ...convertColorPalette(palettes.warning, tokens.colors.warning),
          contrastText: getContrastText(tokens.colors.warning),
        }
      : {
          main: tokens.colors.warning,
          contrastText: getContrastText(tokens.colors.warning),
        },
    success: palettes
      ? {
          ...convertColorPalette(palettes.success, tokens.colors.success),
          contrastText: getContrastText(tokens.colors.success),
        }
      : {
          main: tokens.colors.success,
          contrastText: getContrastText(tokens.colors.success),
        },
    info: {
      main: '#2196f3', // Default MUI info color
      contrastText: '#ffffff',
    },
    background: {
      default: tokens.colors.background,
      paper: tokens.colors.surface,
    },
    text: {
      primary: tokens.colors.text.primary,
      secondary: tokens.colors.text.secondary,
      disabled: 'rgba(0, 0, 0, 0.38)',
    },
    divider: palettes ? palettes.neutral[200] : tokens.colors.neutral,
    grey: palettes
      ? {
          50: palettes.neutral[50],
          100: palettes.neutral[100],
          200: palettes.neutral[200],
          300: palettes.neutral[300],
          400: palettes.neutral[400],
          500: palettes.neutral[500],
          600: palettes.neutral[600],
          700: palettes.neutral[700],
          800: palettes.neutral[800],
          900: palettes.neutral[900],
          A100: palettes.neutral[100],
          A200: palettes.neutral[200],
          A400: palettes.neutral[400],
          A700: palettes.neutral[700],
        }
      : {
          50: '#fafafa',
          100: '#f5f5f5',
          200: '#eeeeee',
          300: '#e0e0e0',
          400: '#bdbdbd',
          500: '#9e9e9e',
          600: '#757575',
          700: '#616161',
          800: '#424242',
          900: '#212121',
          A100: '#f5f5f5',
          A200: '#eeeeee',
          A400: '#bdbdbd',
          A700: '#616161',
        },
  };

  // Create theme with our tokens
  const theme = createTheme({
    palette: paletteOptions,
    typography: {
      fontFamily: tokens.typography.fontFamily.body,
      h1: {
        fontFamily: tokens.typography.fontFamily.heading,
        fontWeight: tokens.typography.fontWeights.bold,
        fontSize: `${tokens.typography.fontSizeScale.xxl}px`,
        lineHeight: tokens.typography.lineHeights.tight,
      },
      h2: {
        fontFamily: tokens.typography.fontFamily.heading,
        fontWeight: tokens.typography.fontWeights.bold,
        fontSize: `${tokens.typography.fontSizeScale.xl}px`,
        lineHeight: tokens.typography.lineHeights.tight,
      },
      h3: {
        fontFamily: tokens.typography.fontFamily.heading,
        fontWeight: tokens.typography.fontWeights.semibold,
        fontSize: `${tokens.typography.fontSizeScale.lg}px`,
        lineHeight: tokens.typography.lineHeights.normal,
      },
      h4: {
        fontFamily: tokens.typography.fontFamily.heading,
        fontWeight: tokens.typography.fontWeights.semibold,
        fontSize: `${tokens.typography.fontSizeScale.md}px`,
        lineHeight: tokens.typography.lineHeights.normal,
      },
      h5: {
        fontFamily: tokens.typography.fontFamily.heading,
        fontWeight: tokens.typography.fontWeights.medium,
        fontSize: `${tokens.typography.fontSizeScale.sm}px`,
        lineHeight: tokens.typography.lineHeights.normal,
      },
      h6: {
        fontFamily: tokens.typography.fontFamily.heading,
        fontWeight: tokens.typography.fontWeights.medium,
        fontSize: `${tokens.typography.fontSizeScale.xs}px`,
        lineHeight: tokens.typography.lineHeights.normal,
      },
      body1: {
        fontFamily: tokens.typography.fontFamily.body,
        fontWeight: tokens.typography.fontWeights.normal,
        fontSize: `${tokens.typography.fontSizeScale.md}px`,
        lineHeight: tokens.typography.lineHeights.normal,
      },
      body2: {
        fontFamily: tokens.typography.fontFamily.body,
        fontWeight: tokens.typography.fontWeights.normal,
        fontSize: `${tokens.typography.fontSizeScale.sm}px`,
        lineHeight: tokens.typography.lineHeights.normal,
      },
      caption: {
        fontFamily: tokens.typography.fontFamily.body,
        fontWeight: tokens.typography.fontWeights.normal,
        fontSize: `${tokens.typography.fontSizeScale.xs}px`,
        lineHeight: tokens.typography.lineHeights.normal,
      },
      overline: {
        fontFamily: tokens.typography.fontFamily.accent,
        fontWeight: tokens.typography.fontWeights.medium,
        fontSize: `${tokens.typography.fontSizeScale.xs}px`,
        lineHeight: tokens.typography.lineHeights.normal,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
      },
    },
    shape: {
      borderRadius: tokens.shape.radiusScale.md,
    },
    spacing: tokens.shape.spacingScale.md / 8, // MUI uses 8px base
    shadows: [
      'none',
      tokens.shape.shadowScale.sm,
      tokens.shape.shadowScale.sm,
      tokens.shape.shadowScale.md,
      tokens.shape.shadowScale.md,
      tokens.shape.shadowScale.md,
      tokens.shape.shadowScale.md,
      tokens.shape.shadowScale.lg,
      tokens.shape.shadowScale.lg,
      tokens.shape.shadowScale.lg,
      tokens.shape.shadowScale.lg,
      tokens.shape.shadowScale.lg,
      tokens.shape.shadowScale.lg,
      tokens.shape.shadowScale.lg,
      tokens.shape.shadowScale.lg,
      tokens.shape.shadowScale.lg,
      tokens.shape.shadowScale.lg,
      tokens.shape.shadowScale.lg,
      tokens.shape.shadowScale.lg,
      tokens.shape.shadowScale.lg,
      tokens.shape.shadowScale.lg,
      tokens.shape.shadowScale.lg,
      tokens.shape.shadowScale.lg,
      tokens.shape.shadowScale.lg,
      tokens.shape.shadowScale.lg,
    ],
    components: {
      // Customize component styles based on our tokens
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: tokens.shape.radiusScale.sm,
            fontWeight: tokens.typography.fontWeights.medium,
            textTransform: 'none', // Don't uppercase buttons
          },
          contained: {
            boxShadow: tokens.shape.shadowScale.sm,
            '&:hover': {
              boxShadow: tokens.shape.shadowScale.md,
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: tokens.shape.radiusScale.md,
            boxShadow: tokens.shape.shadowScale.sm,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: tokens.shape.radiusScale.sm,
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: tokens.shape.radiusScale.lg,
            fontWeight: tokens.typography.fontWeights.medium,
          },
        },
      },
    },
  });

  return theme;
};

/**
 * Generate CSS custom properties from theme tokens
 */
export const generateCSSVariables = (
  tokens: ThemeTokens,
  palettes?: GeneratedColorPalettes | null
): Record<string, string> => {
  const variables: Record<string, string> = {
    // Colors
    '--td-color-primary': tokens.colors.primary,
    '--td-color-secondary': tokens.colors.secondary,
    '--td-color-neutral': tokens.colors.neutral,
    '--td-color-success': tokens.colors.success,
    '--td-color-warning': tokens.colors.warning,
    '--td-color-error': tokens.colors.error,
    '--td-color-background': tokens.colors.background,
    '--td-color-surface': tokens.colors.surface,
    '--td-color-text-primary': tokens.colors.text.primary,
    '--td-color-text-secondary': tokens.colors.text.secondary,
    '--td-color-text-inverse': tokens.colors.text.inverse,

    // Typography
    '--td-font-family-body': tokens.typography.fontFamily.body,
    '--td-font-family-heading': tokens.typography.fontFamily.heading,
    '--td-font-family-accent': tokens.typography.fontFamily.accent,
    '--td-font-size-xs': `${tokens.typography.fontSizeScale.xs}px`,
    '--td-font-size-sm': `${tokens.typography.fontSizeScale.sm}px`,
    '--td-font-size-md': `${tokens.typography.fontSizeScale.md}px`,
    '--td-font-size-lg': `${tokens.typography.fontSizeScale.lg}px`,
    '--td-font-size-xl': `${tokens.typography.fontSizeScale.xl}px`,
    '--td-font-size-xxl': `${tokens.typography.fontSizeScale.xxl}px`,

    // Shape and spacing
    '--td-radius-xs': `${tokens.shape.radiusScale.xs}px`,
    '--td-radius-sm': `${tokens.shape.radiusScale.sm}px`,
    '--td-radius-md': `${tokens.shape.radiusScale.md}px`,
    '--td-radius-lg': `${tokens.shape.radiusScale.lg}px`,
    '--td-radius-xl': `${tokens.shape.radiusScale.xl}px`,
    '--td-spacing-xs': `${tokens.shape.spacingScale.xs}px`,
    '--td-spacing-sm': `${tokens.shape.spacingScale.sm}px`,
    '--td-spacing-md': `${tokens.shape.spacingScale.md}px`,
    '--td-spacing-lg': `${tokens.shape.spacingScale.lg}px`,
    '--td-spacing-xl': `${tokens.shape.spacingScale.xl}px`,
    '--td-spacing-xxl': `${tokens.shape.spacingScale.xxl}px`,
    '--td-shadow-sm': tokens.shape.shadowScale.sm,
    '--td-shadow-md': tokens.shape.shadowScale.md,
    '--td-shadow-lg': tokens.shape.shadowScale.lg,
  };

  // Add palette variables if available
  if (palettes) {
    Object.entries(palettes).forEach(([colorName, palette]) => {
      Object.entries(palette).forEach(([shade, color]) => {
        variables[`--td-color-${colorName}-${shade}`] = color as string;
      });
    });
  }

  return variables;
};

/**
 * Apply CSS variables to document root
 */
export const applyCSSVariables = (variables: Record<string, string>): void => {
  const root = document.documentElement;
  Object.entries(variables).forEach(([property, value]) => {
    root.style.setProperty(property, value);
  });
};
